/**
 * Deleting a client document (product owner decision, 17 September 2026).
 *
 * Deliberately NOT `DeleteWithReasonDialog`. That dialog promises "Nothing is erased",
 * which is true of matters, clients and leads and false here: deleting a document
 * erases the file -- every version of it -- from storage, permanently. What stays is the
 * record that it existed: its name, sizes, checksums, who uploaded it, and who deleted
 * it and why. A person about to press this button has usually found a passport filed
 * against the wrong client, and needs to know exactly which of those two things they
 * are doing.
 *
 * The reason is required here as a courtesy and by the server as a rule. A refusal --
 * a legal hold, a blank reason that slipped past -- shows the server's own words.
 *
 * Two extra warnings, because these are the deletions most likely to be regretted:
 *   - an APPROVED document is evidence somebody already relied on;
 *   - a document answering a REQUEST changes what the client is asked for. One still
 *     awaiting review sends the request back to the client; one that fulfilled a
 *     request leaves it fulfilled, with no file behind it.
 */

import { Form, FormActions, FormTextarea, Modal, useZodForm } from '@legalcrm/ui';
import { z } from 'zod';

const schema = z.object({
  reason: z
    .string()
    .trim()
    .min(3, 'Say why this document is being deleted. It stays on the record permanently.'),
});

type Values = z.infer<typeof schema>;

export interface DeleteDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  /** The document's name as the reader sees it, so two open tabs cannot be confused. */
  name: string;
  approved: boolean;
  linkedToRequest: boolean;
  busy?: boolean;
  error?: unknown;
  onConfirm: (reason: string) => Promise<unknown>;
}

export function DeleteDocumentDialog({
  open,
  onClose,
  name,
  approved,
  linkedToRequest,
  busy,
  error,
  onConfirm,
}: DeleteDocumentDialogProps) {
  const form = useZodForm(schema, { defaultValues: { reason: '' } });

  return (
    <Modal open={open} onClose={onClose} title={`Delete ${name}?`}>
      <Form
        form={form}
        ariaLabel="Delete this document"
        error={error}
        onSubmit={async (values: Values) => {
          await onConfirm(values.reason.trim());
          form.reset();
        }}
      >
        <p className="text-sm text-ink">
          <strong className="font-semibold">
            The file and every earlier version of it will be permanently erased.
          </strong>{' '}
          This cannot be undone and the file cannot be recovered. Nobody at the firm or on
          the client side will be able to open it again.
        </p>

        <p className="text-sm text-ink-muted">
          A record stays: the file name, its size and who uploaded it, along with your name,
          the time and the reason below.
        </p>

        {approved && (
          <div
            role="note"
            className="rounded border border-warning/30 bg-warning-subtle px-3 py-2 text-sm text-ink"
          >
            <strong className="font-semibold">This document has been approved.</strong>{' '}
            Someone at the firm has already accepted it, and work may rely on it.
          </div>
        )}

        {linkedToRequest && (
          <div
            role="note"
            className="rounded border border-warning/30 bg-warning-subtle px-3 py-2 text-sm text-ink"
          >
            <strong className="font-semibold">This document answers a request.</strong>{' '}
            {approved
              ? 'The request stays fulfilled, with no file behind it. Ask the client again if you still need one.'
              : 'If it is still waiting for review, the request goes back to the client to send again.'}
          </div>
        )}

        <FormTextarea
          form={form}
          name="reason"
          label="Why is this document being deleted"
          rows={3}
          autoFocus
          hint="For example: uploaded against the wrong client, or a duplicate."
        />

        <FormActions
          submitLabel="Delete permanently"
          destructive
          busy={busy}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}
